import React from 'react';
import { Box, Button, Link, Paper, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { ReportVisual } from '../../services/reportsApi';
import ReportEmptyState from './ReportEmptyState';

type ReportTimelineProps = {
  visual: ReportVisual;
  compact?: boolean;
  onOpenLink?: (href?: string) => void;
};

function renderTimelineOpenLink(href: string, onOpenLink?: (href?: string) => void) {
  if (onOpenLink) {
    return (
      <Button size="small" variant="text" endIcon={<OpenInNewIcon />} onClick={() => onOpenLink(href)}>
        Open
      </Button>
    );
  }
  return (
    <Button size="small" variant="text" endIcon={<OpenInNewIcon />} href={href}>
      Open
    </Button>
  );
}

const ReportTimeline: React.FC<ReportTimelineProps> = ({ visual, compact = false, onOpenLink }) => {
  const items = Array.isArray(visual.data?.items) ? visual.data.items : [];

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, height: '100%' }}>
      <Typography variant="h6">{visual.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: compact ? 1 : 1.5 }}>
        {visual.insight}
      </Typography>

      {visual.empty_state ? (
        <ReportEmptyState
          title={visual.empty_state.title}
          detail={visual.empty_state.detail}
          missingData={visual.empty_state.missing_data}
          compact={compact}
        />
      ) : (
        <Stack spacing={compact ? 0.75 : 1}>
          {items.map((item: Record<string, unknown>, index: number) => {
            const href = typeof item?.href === 'string' ? item.href : '';
            return (
              <Box
                key={`${String(item?.ts || index)}-${index}`}
                sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 1.5, py: 0.25 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {String(item?.ts || '')}
                </Typography>
                {href ? (
                  <Link href={href} underline="hover" color="inherit">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {String(item?.label || 'Event')}
                    </Typography>
                  </Link>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {String(item?.label || 'Event')}
                  </Typography>
                )}
                {item?.detail ? (
                  <Typography variant="caption" color="text.secondary">
                    {String(item.detail)}
                  </Typography>
                ) : null}
                {href ? (
                  <Box sx={{ mt: 0.5 }}>
                    {renderTimelineOpenLink(href, onOpenLink)}
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

export default ReportTimeline;
